import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import classNames from "classnames";

import ErrorDisplay from "components/ErrorDisplay";
import LinkButton from "components/LinkButton";
import TextareaAutosize from "components/TextareaAutosize";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import LoadingIndicator from "components/LoadingIndicator";
import { useCommentContext } from "widgets/Comments/CommentContext";
import Checkbox from "../Checkbox";

import "./_style.scss";

const CommentForm = ({ isInThread = false, parentCommentId = null }) => {
  const {
    campaignId,
    campaignUpdateId,
    challengerId,
    hasTurnstileError,
    isLoggedIn,
    isRefreshing,
    onInteract,
    openModal,
    onPendingSubmit,
    replyToName,
    isCreating,
    replyToId,
    isError,
  } = useCommentContext();

  const { translate: t } = useLanguageProvider();

  const defaultComment = parentCommentId !== null && replyToName ? `@${replyToName} ` : "";

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { comment: defaultComment, anonymous: false },
  });

  // Reset the form when a comment is successfully posted
  const prevIsCreating = useRef(false);
  useEffect(() => {
    if (prevIsCreating.current && !isCreating) {
      reset({ comment: defaultComment, anonymous: false });
    }
    prevIsCreating.current = isCreating;
  }, [isCreating, reset, defaultComment]);

  // Mirror the original logic: don't show top-level form as loading when a reply is being posted
  const isPosting = parentCommentId === null ? (replyToId !== null ? false : isCreating) : isCreating;

  // Show loading while verifying (logged-in) or while posting
  const isSubmitting = isPosting || (isLoggedIn && isRefreshing);

  const anonymousCommentCopy = t("Publish anonymously");

  const onSubmit = useCallback(
    (data) => {
      // Only open auth modal for guests; logged-in users post inline
      if (!isLoggedIn) {
        openModal();
      }

      const payload = {
        campaign_id: campaignId,
        campaign_update_id: campaignUpdateId,
        challenger_id: challengerId,
        content: data.comment,
        anonymous: Boolean(data.anonymous),
        captchaToken: null,
      };

      if (parentCommentId !== null) {
        payload.reply_to_id = parentCommentId;
      }

      onPendingSubmit(payload);
    },
    [
      campaignId,
      campaignUpdateId,
      challengerId,
      isLoggedIn,
      openModal,
      parentCommentId,
      onPendingSubmit,
    ],
  );

  return (
    <div
      className={classNames({
        "comment-form": true,
        "--reply-mode": parentCommentId !== null,
        "--in-thread": isInThread === true,
        "--v2": true,
      })}
    >
      <form className="comment-form__comment" onSubmit={handleSubmit(onSubmit)}>
        <div className="comment-form__comment-body">
          <Controller
            name="comment"
            control={control}
            rules={{ validate: (v) => (v && v.trim().length > 0) || "Comment cannot be empty" }}
            render={({ field }) => (
              <ErrorDisplay
                alwaysShowErrors={!!errors.comment}
                field={{ error: errors.comment?.message }}
              >
                <TextareaAutosize
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  onFocus={onInteract}
                  className="comment-form__textarea"
                  placeholder={t("Write a comment...")}
                />
              </ErrorDisplay>
            )}
          />

          <div className="comment-form__post-actions">
            <Controller
              name="anonymous"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  checkedLabel={anonymousCommentCopy}
                  disabled={isPosting}
                  uncheckedLabel={anonymousCommentCopy}
                  labelSize="regular"
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />

            <div className="comment-form__action">
              {campaignUpdateId ? (
                <button
                  type="submit"
                  className="campaign-update__donate-btn"
                  disabled={isSubmitting || hasTurnstileError}
                >
                  {isSubmitting ? t("Posting...") : t("Post")}
                  {isSubmitting && (
                    <LoadingIndicator size="1em" className="button__loading-indicator" />
                  )}
                </button>
              ) : (
                <LinkButton
                  isBold
                  isLoading={isSubmitting}
                  isUppercase
                  look="primary"
                  onClick={handleSubmit(onSubmit)}
                  disabled={hasTurnstileError}
                  size="small"
                >
                  {t("Post")}
                </LinkButton>
              )}
            </div>
          </div>

          {isError && (
            <div
              className={`comment-form__error ${campaignUpdateId ? "--campaign-update" : ""}`}
            >
              Unable to post your comment at this time
            </div>
          )}

          {hasTurnstileError && (
            <div className="comment-form__error">
              Unable to post your comment at this time, please refresh the page and try again
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

CommentForm.propTypes = {
  isInThread: PropTypes.bool,
  parentCommentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default CommentForm;
